// FILE: client/src/pages/secure/BuyerAgencyAgreementPage.js
// (This is a NEW file)

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';

const BuyerAgencyAgreementPage = () => {
    const [clients, setClients] = useState([]);
    const [formData, setFormData] = useState({
        client_id: '',
        start_date: new Date().toISOString().split('T')[0],
        end_date: '',
        geographic_area: '',
        property_type: 'Residential',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [generatedFile, setGeneratedFile] = useState(null);
    const apiClient = useApiClient();

    useEffect(() => {
        // Fetch clients to populate the dropdown
        apiClient.get('/api/clients').then(response => {
            // Applying the same fix as before to prevent the 'map' error
            if (Array.isArray(response)) {
                setClients(response);
            } else {
                setClients([]);
                console.error("API response for clients was not an array:", response);
            }
        }).catch(err => {
            console.error("Failed to fetch clients", err);
            setClients([]); // Ensure clients is always an array
        });
    }, [apiClient]);

    const handleChange = (e) => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setGeneratedFile(null);
        try {
            const response = await apiClient.post('/api/forms/generate-buyer-agency', formData, { responseType: 'blob' });
            
            const file = new Blob([response.data], { type: 'application/pdf' });
            const fileURL = URL.createObjectURL(file);
            setGeneratedFile(fileURL);

        } catch (error) {
            console.error("Failed to generate agreement", error);
            alert(error.response?.data?.message || 'An error occurred.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="p-4 md:p-8">
            <Link to="/dashboard/forms" className="text-indigo-600 hover:underline mb-6 inline-block">&larr; Back to Forms Portal</Link>
            <div className="max-w-4xl mx-auto bg-white p-8 rounded-lg shadow-lg">
                <h1 className="text-2xl font-bold text-slate-800 mb-2">Exclusive Buyer Representation Agreement</h1>
                <p className="text-slate-500 mb-6">Fill out the details below to generate a new agreement for a buyer client.</p>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <label htmlFor="client_id" className="block text-sm font-medium text-gray-700">Select Client</label>
                        <select id="client_id" name="client_id" value={formData.client_id} onChange={handleChange} required className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm">
                            <option value="">-- Choose a client --</option>
                            {clients.map(client => (
                                <option key={client.client_id} value={client.client_id}>{client.first_name} {client.last_name}</option>
                            ))}
                        </select>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label htmlFor="start_date" className="block text-sm font-medium text-gray-700">Agreement Start Date</label>
                            <input type="date" id="start_date" name="start_date" value={formData.start_date} onChange={handleChange} required className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm" />
                        </div>
                        <div>
                            <label htmlFor="end_date" className="block text-sm font-medium text-gray-700">Agreement End Date</label>
                            <input type="date" id="end_date" name="end_date" value={formData.end_date} onChange={handleChange} required className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm" />
                        </div>
                    </div>

                    <div>
                        <label htmlFor="geographic_area" className="block text-sm font-medium text-gray-700">Geographic Area</label>
                        <input type="text" id="geographic_area" name="geographic_area" value={formData.geographic_area} onChange={handleChange} required placeholder="e.g., Cook and DuPage Counties" className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm" />
                    </div>
                    
                    <div className="flex justify-end">
                        <button type="submit" disabled={isSubmitting} className="px-6 py-2 bg-slate-800 text-white rounded-md disabled:bg-indigo-400">
                            {isSubmitting ? 'Generating...' : 'Generate Agreement PDF'}
                        </button>
                    </div>
                </form>

                {generatedFile && (
                    <div className="mt-8 border-t pt-6">
                        <h3 className="text-lg font-bold">Agreement Generated Successfully</h3>
                        <a href={generatedFile} download={`BuyerAgreement_${formData.client_id}.pdf`} className="inline-block px-6 py-2 bg-green-600 text-white rounded-md">
                            Download PDF
                        </a>
                        <div className="mt-4 h-96">
                           <iframe src={generatedFile} width="100%" height="100%" title="Generated Agreement Preview"></iframe>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BuyerAgencyAgreementPage;
